import { sumBy, totalsMoneyPlain } from 'components/TableTotals'
import { itemCountPerPage } from 'config'
import { useEffect, useMemo, useState } from 'react'
import { getLoanCharges } from 'services'
import { Checkbox, Input2, Modal, PlainTable } from 'stories/components'
import { formatDate, removeComma, thousandSeperator } from 'utils'

import { ILoanCharge, LoanChargePaidStatus } from '../LoanCharges'

/** One charge this payment pays, and how much of the money goes to each part of it. */
export interface IPayChargeItem {
  id: number
  unpaid: number
  accrued: number
}

/**
 * What the payment collected in charges: the totals the Applies To > Charges line shows, plus the
 * charges behind them. Saving the payment settles each named charge by its own amounts, so several
 * charges can be paid on one payment.
 */
export interface IPayCharges {
  unpaid: number
  accrued: number
  items: IPayChargeItem[]
}

const num = (value: any) => Number(removeComma(value ?? 0)) || 0
const round2 = (value: number) => Math.round(value * 100) / 100

/**
 * Pick the charges a payment collects, and how much of each.
 *
 * `asOf` is the date the charges are priced to - the payment's own Payment Received date. A charge
 * carrying a rate accrues interest daily, so a payment keyed for an earlier day has to be offered
 * the interest standing on that day: billing today's figure asks the borrower for interest that
 * was not yet owed when they paid. Omitted prices them to today, which is what the Charges screen
 * shows.
 */
export const LoanChargesModal = ({ onClose, asOf }: { onClose: Function; asOf?: string }) => {
  const [loading, setLoading] = useState(false)
  const [data, setData] = useState<ILoanCharge[]>([])
  // Charge id -> what to pay on it. A charge is selected exactly when it has an entry here; the
  // amounts start at everything the charge is carrying and are editable for a partial payment.
  const [selected, setSelected] = useState<Record<number, { unpaid: string; accrued: string }>>({})

  const refetch = () => {
    setLoading(true)
    getLoanCharges({
      orderBy: 'createdAt',
      orderDir: '-1',
      isLoan: true,
      pageNum: 0,
      count: itemCountPerPage,
      // Only the charges still owed. A charge paid in full, waived, or cleared wholesale by a
      // payoff has nothing left to collect, so offering it here is an invitation to key a payment
      // against a settled fee - and the picker lists a loan's charges unpaged, so the paid ones
      // pushed the live ones down a long list. Same reading as the Charges grid's Unpaid filter.
      paidStatus: LoanChargePaidStatus.Unpaid,
      // A whole date only. The payment screen's date field reports every keystroke, and the
      // backend reads a partial value as "no date" and falls back to today - sending one would
      // quote today's interest while the screen said otherwise.
      asOf: /^\d{4}-\d{2}-\d{2}$/.test(asOf || '') ? asOf : undefined,
    })
      .then((data) => {
        setData(data.results)
      })
      .finally(() => setLoading(false))
  }

  // Re-read on a date change as well as on open: the amounts are priced to `asOf`, so a stale
  // fetch would seed the selection with another day's interest.
  useEffect(() => refetch(), [asOf])

  const onToggle = (charge: ILoanCharge, checked: boolean) => {
    setSelected((prev) => {
      const next = { ...prev }
      if (checked)
        next[charge.id] = {
          unpaid: String(num(charge.unpaidBalance)),
          accrued: String(num(charge.accruedInterest)),
        }
      else delete next[charge.id]
      return next
    })
  }

  const onAmount = (id: number, key: 'unpaid' | 'accrued', value: string) => {
    setSelected((prev) => (prev[id] ? { ...prev, [id]: { ...prev[id], [key]: value } } : prev))
  }

  // Only the charges with money against them are paid: an amount edited to zero pays nothing and
  // must not settle the charge.
  const payCharges: IPayCharges = useMemo(() => {
    const items = Object.keys(selected)
      .map((id) => ({
        id: Number(id),
        unpaid: round2(num(selected[Number(id)].unpaid)),
        accrued: round2(num(selected[Number(id)].accrued)),
      }))
      .filter((item) => item.unpaid + item.accrued > 0)
      .sort((a, b) => a.id - b.id)
    return {
      unpaid: round2(items.reduce((sum, item) => sum + item.unpaid, 0)),
      accrued: round2(items.reduce((sum, item) => sum + item.accrued, 0)),
      items,
    }
  }, [selected])

  const tableData = useMemo(
    () =>
      data.map((item, index) => {
        // A charge carrying nothing is already settled, so there is nothing to pay on it.
        const payable = round2(num(item.unpaidBalance) + num(item.accruedInterest)) > 0
        return [
          <Checkbox
            id={`payCharge-${item.id}`}
            title=""
            value={!!selected[item.id]}
            disabled={!payable}
            onChange={(checked: boolean) => onToggle(item, checked)}
          />,
          index + 1,
          formatDate(item.date),
          item.description,
          thousandSeperator(item.interestRate),
          formatDate(item.interestFromDate),
          item.deferred ? 'Yes' : 'No',
          thousandSeperator(item.originalBalance),
          thousandSeperator(item.unpaidBalance),
          thousandSeperator(item.accruedInterest),
          thousandSeperator(item.totalDue),
        ]
      }),
    [data, selected],
  )

  // The amounts sit under the table rather than in it: the charge table is wider than the modal, so
  // an input in a trailing column would be scrolled out of sight.
  const renderAmounts = () => {
    const picked = data.filter((item) => selected[item.id])
    if (!picked.length) return null
    return (
      <div className="mt-5">
        <p className="mb-2 font-semibold">Amounts to pay</p>
        {picked.map((item, index) => (
          <div key={item.id} className="flex items-center gap-4 mb-3">
            <p className="w-64 shrink-0">
              <b>{data.indexOf(item) + 1}</b>. {item.description}
            </p>
            <div className="w-40">
              <Input2
                title="Unpaid"
                type="thousandSep"
                prefix="$"
                autoFocus={index === 0}
                value={selected[item.id].unpaid}
                onChange={(value: string) => onAmount(item.id, 'unpaid', value)}
              />
            </div>
            <div className="w-40">
              <Input2
                title="Accrued"
                type="thousandSep"
                prefix="$"
                value={selected[item.id].accrued}
                onChange={(value: string) => onAmount(item.id, 'accrued', value)}
              />
            </div>
            <p className="text-gray-500">
              of ${thousandSeperator(round2(num(item.unpaidBalance) + num(item.accruedInterest)).toFixed(2))} due
            </p>
          </div>
        ))}
      </div>
    )
  }

  const onSubmit = () => {
    if (!payCharges.items.length) return
    onClose(payCharges)
  }

  return (
    <Modal
      title="Loan Charges"
      loading={loading}
      isOpen
      onClose={() => onClose(null)}
      disabled={!payCharges.items.length}
      onOk={onSubmit}
    >
      <div>
        <p className="mb-3 text-sm text-gray-500">
          Tick every charge this payment pays. Each one starts at its full balance and interest; edit either amount to
          pay part of a charge. Saving the payment brings the balances down.
        </p>
        <PlainTable
          header={[
            'Pay',
            'No',
            'Date',
            'Description',
            'Interest Rate',
            'Interest From Date',
            'Deferred',
            'Original Balance',
            'Unpaid Balance',
            'Accrued Interest',
            'Total Due',
          ]}
          data={tableData}
          footer={
            data.length
              ? [
                  'Totals',
                  '',
                  '',
                  '',
                  '',
                  '',
                  '',
                  totalsMoneyPlain(sumBy(data, 'originalBalance')),
                  totalsMoneyPlain(sumBy(data, 'unpaidBalance')),
                  totalsMoneyPlain(sumBy(data, 'accruedInterest')),
                  totalsMoneyPlain(sumBy(data, 'totalDue')),
                ]
              : undefined
          }
        />
        {renderAmounts()}
        <p className="mt-4">
          Paying <b>{payCharges.items.length}</b> charge{payCharges.items.length === 1 ? '' : 's'}: $
          {thousandSeperator(round2(payCharges.unpaid + payCharges.accrued).toFixed(2))}
          {payCharges.accrued > 0
            ? ` (Unpaid $${thousandSeperator(payCharges.unpaid.toFixed(2))}, Accrued $${thousandSeperator(
                payCharges.accrued.toFixed(2),
              )})`
            : ''}
        </p>
      </div>
    </Modal>
  )
}
